import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiAlertTriangle, FiCheckCircle, FiRefreshCw, FiSmartphone, FiX } from 'react-icons/fi';
import api from '../../libs/axios';
import SetAsideImportPanel from './set-aside-import-panel';

// DECISION-004 / DECISION-007: who holds scoring for a match, and the admin's ways to move
// it. One phone scores each match; a phone that lost scoring is "fenced" and its later
// uploads are kept aside for review. Every action needs a reason and is logged.
// Never shows device ids or phone numbers: the server sends a short phone tag only.

interface Holder {
  user_id: number | null;
  name: string | null;
  device_tag: string | null;
  connected: boolean;
  last_seen_at: string | null;
  silent_seconds: number | null;
  left_at: string | null;
  epoch: number | null;
}
interface Fence {
  device_tag: string | null;
  user_id: number | null;
  at: string | null;
  server_counter_then: number | null;
  reason: string | null;
}
interface WaitState {
  active: boolean;
  since: string | null;
  from_counter: number | null;
  balls_added: number;
  for_name: string | null;
}
interface Official { user_id: number; name: string | null; role: string | null }
interface SetAsideBall {
  log_id: number;
  user_id: number | null;
  at: string | null;
  source: string | null;
  event_counter: number | null;
  over: number | null;
  ball: number | null;
  inning_number: number | null;
  runs: number | null;
  ball_type: string | null;
  is_wicket: boolean | null;
}
interface LeaseView {
  mode: string;
  configured_mode: string | null;
  holder: Holder | null;
  takeover_after_seconds: number | null;
  auto_expire_minutes: number | null;
  fenced: Fence[];
  handover_pending: boolean;
  wait: WaitState | null;
  officials: Official[];
  set_aside_balls: SetAsideBall[];
}

type Action = 'move' | 'release' | 'clear_fences';

interface Props {
  matchId: string;
  isOpen: boolean;
  onClose: () => void;
  onDone?: () => void;
}

const errorMessage = (e: any, fallback: string) => e?.response?.data?.message || fallback;

const ist = (iso: string | null) => {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return iso; }
};

const num = (v: any): number | null => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v));
const str = (v: any): string | null => (v === null || v === undefined || v === '' ? null : String(v));

// Parse the GET /match-ops/:id/lease answer defensively: older servers (release 8/9) have
// no `wait` and no officials list; field names for those follow DECISION-007 section 4.
const parseView = (d: any): LeaseView => {
  const h = d?.holder;
  const w = d?.wait ?? d?.scorer_wait ?? null;
  const officialsRaw: any[] = d?.officials ?? d?.eligible_officials ?? d?.eligible_scorers ?? [];
  return {
    mode: String(d?.mode ?? 'off'),
    configured_mode: str(d?.configured_mode),
    holder: h ? {
      user_id: num(h.user_id),
      name: str(h.name),
      device_tag: str(h.device_tag),
      connected: !!h.connected,
      last_seen_at: str(h.last_seen_at),
      silent_seconds: num(h.silent_seconds),
      left_at: str(h.left_at),
      epoch: num(h.epoch),
    } : null,
    takeover_after_seconds: num(d?.takeover_after_seconds),
    auto_expire_minutes: num(d?.auto_expire_minutes),
    fenced: (Array.isArray(d?.fenced) ? d.fenced : []).map((f: any) => ({
      device_tag: str(f?.device_tag),
      user_id: num(f?.user_id),
      at: str(f?.at),
      server_counter_then: num(f?.server_counter_then ?? f?.c),
      reason: str(f?.reason),
    })),
    handover_pending: !!d?.handover_pending,
    wait: w && (w.active ?? true) ? {
      active: true,
      since: str(w.since ?? w.at),
      from_counter: num(w.from_counter ?? w.from),
      balls_added: num(w.balls_added) ?? 0,
      for_name: str(w.for_name),
    } : null,
    officials: (Array.isArray(officialsRaw) ? officialsRaw : [])
      .map((o: any) => ({ user_id: Number(o?.user_id ?? o?.id), name: str(o?.name ?? o?.player_name ?? o?.first_name), role: str(o?.role) }))
      .filter((o: Official) => Number.isInteger(o.user_id) && o.user_id > 0),
    set_aside_balls: (Array.isArray(d?.set_aside_balls) ? d.set_aside_balls : []).map((b: any) => ({
      log_id: Number(b?.log_id),
      user_id: num(b?.user_id),
      at: str(b?.at),
      source: str(b?.source),
      event_counter: num(b?.event_counter),
      over: num(b?.over),
      ball: num(b?.ball),
      inning_number: num(b?.inning_number),
      runs: num(b?.runs),
      ball_type: str(b?.ball_type),
      is_wicket: b?.is_wicket == null ? null : !!b.is_wicket,
    })),
  };
};

const ballSummary = (b: SetAsideBall) => {
  const runs = b.runs ?? 0;
  const t = (b.ball_type ?? 'legal').toLowerCase();
  let s: string;
  if (t === 'wide') s = runs > 0 ? `Wd+${runs}` : 'Wd';
  else if (t === 'no_ball') s = runs > 0 ? `Nb+${runs}` : 'Nb';
  else if (t === 'bye') s = `${runs}b`;
  else if (t === 'leg_bye') s = `${runs}lb`;
  else s = String(runs);
  return b.is_wicket ? `${s} W` : s;
};

const reasonLabel: Record<string, string> = {
  takeover: 'taken over', expired: 'expired', left: 'left scoring', admin_move: 'moved by admin',
  admin_release: 'released by admin', transfer: 'transferred', shadow_claim: 'claimed (shadow)', handover: 'handed over',
};

const ScoringPhoneModal: React.FC<Props> = ({ matchId, isOpen, onClose, onDone }) => {
  const [view, setView] = useState<LeaseView | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [action, setAction] = useState<Action>('move');
  const [toUser, setToUser] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [doneMessage, setDoneMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res: any = await api.get(`/match-ops/${matchId}/lease`);
      setView(parseView(res.data));
    } catch (e: any) {
      setView(null);
      setLoadError(errorMessage(e, 'Could not load the scoring phone for this match.'));
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    if (!isOpen) return;
    setAction('move');
    setToUser('');
    setReason('');
    setDoneMessage(null);
    load();
  }, [isOpen, load]);

  if (!isOpen) return null;

  const reasonOk = reason.trim().length >= 5;
  const toUserId = Number(toUser);
  const toUserOk = action !== 'move' || (Number.isInteger(toUserId) && toUserId > 0);
  const canSubmit = !!view && reasonOk && toUserOk && !submitting && !loading;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let res: any;
      if (action === 'clear_fences') {
        res = await api.post(`/match-ops/${matchId}/lease/clear-fences`, { reason: reason.trim() });
      } else {
        res = await api.post(`/match-ops/${matchId}/scorer`, {
          user_id: action === 'move' ? toUserId : null,
          reason: reason.trim(),
        });
      }
      const msg = res?.message || (action === 'move' ? 'Scoring moved' : action === 'release' ? 'Scoring released' : 'Fences cleared');
      toast.success(msg);
      setDoneMessage(msg);
      setReason('');
      onDone?.();
      load();
    } catch (e: any) {
      toast.error(errorMessage(e, 'Action failed'), { duration: 8000 });
    } finally {
      setSubmitting(false);
    }
  };

  const h = view?.holder;
  const holderLine = () => {
    if (!h || h.user_id == null) return 'Nobody holds scoring. The next eligible phone to open the scoring screen takes it.';
    const who = `${h.name || `User #${h.user_id}`}${h.device_tag ? ` (phone ${h.device_tag})` : ''}`;
    if (h.connected) return `${who} · connected now`;
    const seen = h.last_seen_at ? `last seen ${ist(h.last_seen_at)}` : 'not seen yet';
    const silent = h.silent_seconds != null ? ` (${h.silent_seconds >= 120 ? `${Math.round(h.silent_seconds / 60)} min` : `${h.silent_seconds} s`} ago)` : '';
    return `${who} · ${seen}${silent}${h.left_at ? ` · left the scoring screen ${ist(h.left_at)}` : ''}`;
  };

  const actionHelp: Record<Action, string> = {
    move: 'Scoring moves to this account now. Its first phone to open the match scores. The current phone is stopped and its later uploads are kept aside.',
    release: 'Force log-out of the scoring phone for this match only (its account stays signed in). The next eligible phone may take scoring at once. The current phone is stopped and its later uploads are kept aside.',
    clear_fences: 'Lets phones that lost scoring claim it again. Their unsent balls will then upload into the match, so do this only after the set-aside balls are reviewed.',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={submitting ? undefined : onClose} />
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl">
          <button onClick={onClose} disabled={submitting} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50" aria-label="Close">
            <FiX className="w-5 h-5" />
          </button>
          <div className="p-6 space-y-5">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <FiSmartphone className="w-5 h-5 text-primary-600" />
                <h3 className="text-lg font-semibold text-gray-900">Scoring phone · match #{matchId}</h3>
                <button onClick={load} disabled={loading || submitting} className="ml-auto mr-8 text-xs text-primary-700 hover:underline disabled:opacity-50 flex items-center gap-1">
                  <FiRefreshCw className="w-3 h-3" /> Refresh
                </button>
              </div>
              {view && (
                <p className="text-xs text-gray-500">
                  Lease {view.mode}{view.configured_mode && view.configured_mode !== view.mode ? ` (configured ${view.configured_mode})` : ''}
                  {view.takeover_after_seconds != null ? ` · take over after ${view.takeover_after_seconds} s` : ''}
                  {view.auto_expire_minutes != null ? ` · moves by itself after ${view.auto_expire_minutes} min` : ''}
                </p>
              )}
            </div>

            {loading && !view && <p className="text-sm text-gray-500">Loading…</p>}
            {loadError && <p className="text-sm text-red-600">{loadError}</p>}

            {view && (
              <>
                {view.mode !== 'on' && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                    <FiAlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>The scoring lease is {view.mode}: a second phone is not stopped. Release clears the scorer lock only.</span>
                  </div>
                )}

                <div className="rounded-lg border border-gray-200 p-4 text-sm">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Holds scoring</div>
                  <p className="text-gray-900">{holderLine()}</p>
                  {view.handover_pending && <p className="text-xs text-amber-700 mt-1">A hand-over request is waiting for the scoring phone&apos;s answer.</p>}
                </div>

                {view.wait && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                    <div className="font-medium">Waiting for the old phone to upload</div>
                    <p className="mt-1">
                      Since {ist(view.wait.since)}{view.wait.for_name ? ` · waiting for ${view.wait.for_name}'s phone` : ''}
                      {view.wait.from_counter != null ? ` · continues from ball #${view.wait.from_counter}` : ''}
                      {` · ${view.wait.balls_added} ball${view.wait.balls_added === 1 ? '' : 's'} added so far`}
                    </p>
                    <p className="text-xs mt-1">The scoring phone chose to wait. It ends when the scorer taps Continue, scores a ball, or scoring moves.</p>
                  </div>
                )}

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">Phones that lost scoring ({view.fenced.length})</h4>
                  {view.fenced.length === 0 ? <p className="text-xs text-gray-400">None.</p> : (
                    <div className="overflow-hidden rounded-lg border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600"><tr>
                          <th className="text-left font-medium px-3 py-2">Phone</th>
                          <th className="text-left font-medium px-3 py-2">User</th>
                          <th className="text-left font-medium px-3 py-2">When (IST)</th>
                          <th className="text-left font-medium px-3 py-2">Why</th>
                          <th className="text-right font-medium px-3 py-2">Server ball then</th>
                        </tr></thead>
                        <tbody>
                          {view.fenced.map((f, i) => (
                            <tr key={`${f.device_tag}-${f.at}-${i}`} className="border-t border-gray-100">
                              <td className="px-3 py-2">{f.device_tag ?? '-'}</td>
                              <td className="px-3 py-2">{f.user_id != null ? `#${f.user_id}` : '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap">{ist(f.at)}</td>
                              <td className="px-3 py-2">{f.reason ? (reasonLabel[f.reason] ?? f.reason) : '-'}</td>
                              <td className="px-3 py-2 text-right">{f.server_counter_then != null ? `#${f.server_counter_then}` : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Set-aside balls ({view.set_aside_balls.length})</h4>
                  <p className="text-xs text-gray-500 mb-2">Balls uploaded by a phone that did not hold scoring. Kept in full for review; not in the match.</p>
                  {view.set_aside_balls.length === 0 ? <p className="text-xs text-gray-400">None.</p> : (
                    <div className="overflow-auto max-h-64 rounded-lg border border-gray-200">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600 sticky top-0"><tr>
                          <th className="text-left font-medium px-3 py-2">Ball #</th>
                          <th className="text-left font-medium px-3 py-2">Innings</th>
                          <th className="text-left font-medium px-3 py-2">Over</th>
                          <th className="text-left font-medium px-3 py-2">Ball</th>
                          <th className="text-left font-medium px-3 py-2">User</th>
                          <th className="text-left font-medium px-3 py-2">When (IST)</th>
                        </tr></thead>
                        <tbody>
                          {view.set_aside_balls.map((b) => (
                            <tr key={b.log_id} className="border-t border-gray-100">
                              <td className="px-3 py-2">{b.event_counter ?? '-'}</td>
                              <td className="px-3 py-2">{b.inning_number ?? '-'}</td>
                              <td className="px-3 py-2">{b.over != null && b.ball != null ? `${b.over}.${b.ball}` : '-'}</td>
                              <td className="px-3 py-2">{ballSummary(b)}</td>
                              <td className="px-3 py-2">{b.user_id != null ? `#${b.user_id}` : '-'}</td>
                              <td className="px-3 py-2 whitespace-nowrap">{ist(b.at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {view.set_aside_balls.length > 0 && (
                  <SetAsideImportPanel matchId={matchId} onChanged={() => { onDone?.(); load(); }} />
                )}

                <div className="border-t border-gray-100 pt-4 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-900">Change who scores</h4>
                  <div className="flex flex-wrap gap-4 text-sm">
                    {([
                      ['move', 'Move scoring to…'],
                      ['release', 'Release scoring (force log-out)'],
                      ['clear_fences', 'Clear phones that lost scoring'],
                    ] as [Action, string][]).map(([a, label]) => (
                      <label key={a} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="lease-action" checked={action === a} onChange={() => setAction(a)} disabled={submitting} />
                        <span>{label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">{actionHelp[action]}</p>

                  {action === 'move' && (
                    <div className="flex flex-wrap gap-3 items-end">
                      {view.officials.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Official of this match</label>
                          <select value={view.officials.some((o) => String(o.user_id) === toUser) ? toUser : ''}
                            onChange={(e) => setToUser(e.target.value)} disabled={submitting}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white min-w-[16rem]">
                            <option value="">Choose…</option>
                            {view.officials.map((o) => (
                              <option key={o.user_id} value={String(o.user_id)}>
                                {(o.name || `User #${o.user_id}`)}{o.role ? ` · ${o.role}` : ''} · #{o.user_id}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{view.officials.length > 0 ? 'or user ID' : 'User ID'}</label>
                        <input value={toUser} onChange={(e) => setToUser(e.target.value.replace(/[^0-9]/g, ''))} placeholder="e.g. 5826" disabled={submitting}
                          className="rounded-lg border border-gray-300 px-3 py-2 text-sm w-32" />
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-1" htmlFor="lease-reason">
                      Reason <span className="font-normal text-gray-500">(kept in the audit log)</span>
                    </label>
                    <textarea id="lease-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} rows={2} disabled={submitting}
                      placeholder="e.g. Scorer's phone battery dead; organiser asked to move scoring to Ravi"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    {!reasonOk && reason.length > 0 && <p className="text-xs text-gray-500 mt-1">At least 5 characters.</p>}
                  </div>

                  {doneMessage && (
                    <div className="flex items-start gap-2 rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-900">
                      <FiCheckCircle className="w-4 h-4 mt-0.5" />
                      <span>{doneMessage}</span>
                    </div>
                  )}

                  <div className="flex justify-end gap-3">
                    <button onClick={onClose} disabled={submitting} className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 disabled:opacity-50">
                      Close
                    </button>
                    <button onClick={submit} disabled={!canSubmit}
                      className={`px-4 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${action === 'move' ? 'bg-primary-600 hover:bg-primary-700' : 'bg-red-600 hover:bg-red-700'}`}>
                      {submitting ? 'Working…' : action === 'move' ? 'Move scoring' : action === 'release' ? 'Release scoring' : 'Clear phones'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoringPhoneModal;
